/**
 * OtAuditModal — "Audit OT" reconciliation report for one worker × one month.
 *
 * Explains any delta between the three OT numbers an admin can see in the UI:
 *   • weekly_rule   — pure 37.5h weekly rule (what /week shows)
 *   • actual        — what /admin/day-ot & /month show (daily_mode on spilling
 *                     weeks so the closing month pays its OT now)
 *   • paid_in_month — what the payslip pays this month (locked days only)
 *
 * Also lists entries edited after creation and the full edit log kept from
 * Nov 2026 onwards (prior values of minutes / project / from–to).
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, AlertTriangle, CheckCircle2, Lock, Unlock, Clock, Pencil, FileSpreadsheet, History, HelpCircle, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TS = `${API}/doserne/timesheet`;

const WD_SHORT = ["Lun", "Mar", "Mie", "Joi", "Vin", "Sâm", "Dum"];

const fmtH = (min) => {
  const sign = min < 0 ? "-" : "";
  const v = Math.abs(min);
  const h = Math.floor(v / 60);
  const m = v % 60;
  return m === 0 ? `${sign}${h}h` : `${sign}${h}h${String(m).padStart(2, "0")}`;
};
const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso + "T00:00:00").toLocaleDateString("ro-RO", { day: "2-digit", month: "short" }); }
  catch { return iso; }
};
const fmtTs = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("ro-RO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export default function OtAuditModal({ open, onClose, members, month, authHeader, defaultEmail }) {
  const [email, setEmail] = useState(defaultEmail || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (defaultEmail) { setEmail(defaultEmail); return; }
    if (!email && members && members.length > 0) setEmail(members[0].email);
  }, [open, defaultEmail, members, email]);

  useEffect(() => {
    if (!open || !email) { setData(null); return; }
    const run = async () => {
      setLoading(true);
      try {
        const { data: d } = await axios.get(`${TS}/admin/ot-audit`, { ...authHeader(), params: { email, month } });
        setData(d);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Nu am putut încărca auditul");
        setData(null);
      } finally { setLoading(false); }
    };
    run();
  }, [open, email, month, authHeader]);

  const downloadPdf = async () => {
    if (!email) return;
    setDownloading(true);
    try {
      const res = await axios.get(`${TS}/admin/ot-audit/pdf`, {
        ...authHeader(), params: { email, month }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit_OT_${email}_${month}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF descărcat");
    } catch (e) {
      toast.error("PDF-ul nu a putut fi generat");
    } finally { setDownloading(false); }
  };

  const totalDelta = useMemo(() => {
    if (!data) return { weeklyActual: 0, actualPaid: 0 };
    return {
      weeklyActual: data.deltas?.weekly_vs_actual_minutes || 0,
      actualPaid: data.deltas?.actual_vs_paid_minutes || 0,
    };
  }, [data]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[90] bg-slate-900/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-auto" data-testid="ot-audit-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl my-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-gradient-to-r from-cyan-50 via-white to-amber-50">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-cyan-700" />
            <h2 className="text-base font-black text-slate-900">Audit OT — reconciliere ore suplimentare</h2>
            <button type="button" onClick={() => setInfoOpen(true)} data-testid="ot-audit-info-btn"
              title="Vezi toate criteriile de calcul OT"
              className="w-7 h-7 rounded-lg hover:bg-cyan-100 flex items-center justify-center text-cyan-700">
              <HelpCircle className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={downloadPdf} disabled={!email || downloading} data-testid="ot-audit-pdf-btn"
              title="Descarcă audit PDF pentru contabil"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-black disabled:opacity-50">
              {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              Descarcă PDF
            </button>
            <button type="button" onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600" data-testid="ot-audit-close"><X className="w-4 h-4" /></button>
          </div>
        </div>
        {infoOpen && <OtRulesInfo onClose={() => setInfoOpen(false)} />}

        {/* Filters */}
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
          <label className="text-[11px] font-black uppercase tracking-wider text-slate-500">Angajat</label>
          <select value={email} onChange={(e) => setEmail(e.target.value)} className="h-9 px-2 rounded-lg border border-slate-200 bg-white text-sm font-bold text-slate-800" data-testid="ot-audit-member-select">
            <option value="">— alege —</option>
            {(members || []).map((m) => (<option key={m.email} value={m.email}>{m.name || m.email}</option>))}
          </select>
          <span className="h-5 w-px bg-slate-200" />
          <span className="text-[11px] font-black uppercase tracking-wider text-slate-500">Luna</span>
          <span className="h-9 px-2 rounded-lg border border-slate-200 bg-slate-50 inline-flex items-center text-sm font-black text-slate-900">{month}</span>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400 ml-auto" />}
        </div>

        {/* Content */}
        {!data && !loading && (
          <div className="p-10 text-center text-sm text-slate-500">Alege un angajat ca să încarci raportul.</div>
        )}
        {loading && (
          <div className="p-10 text-center text-sm text-slate-500">Se calculează…</div>
        )}

        {data && !loading && (
          <div className="p-5 space-y-4">
            {/* Summary strip */}
            <SummaryStrip data={data} totalDelta={totalDelta} />

            {/* Cost estimate — how the hours translate to employer cost */}
            <CostCard data={data} />

            {/* Per-week breakdown */}
            <div className="space-y-3">
              {data.weeks.map((wk) => (<WeekCard key={wk.monday} wk={wk} />))}
            </div>

            {/* Edit-log drawer */}
            <EditLogSection weeks={data.weeks} />
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryStrip({ data, totalDelta }) {
  const T = data.month_totals;
  const noDelta = totalDelta.weeklyActual === 0 && totalDelta.actualPaid === 0;
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid="ot-audit-summary">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <TotalBox label="Regula săptămânală (37.5h)" tooltip="Ce ar plăti exclusiv regula 37.5h/săpt (ce vede workerul în vederea sa pe săptămână)" t={T.weekly_rule} tone="slate" />
        <TotalBox label="Calcul final (actual)" tooltip="Ce arată rapoartele lunare și fluturașul: cu daily_mode pe ultima săptămână care debordează" t={T.actual_in_month} tone="cyan" />
        <TotalBox label="OT plătit (zile blocate)" tooltip="Doar zilele blocate contează pe fluturaș — restul OT este calculat dar neplătit încă" t={T.paid_in_month} tone="emerald" />
      </div>
      <div className="mt-3 space-y-1.5">
        {data.summary.map((s, i) => (
          <div key={i} className={`flex items-start gap-2 text-xs font-bold ${noDelta ? "text-emerald-700" : "text-amber-800"}`}>
            {noDelta ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />}
            <span>{s}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TotalBox({ label, tooltip, t, tone }) {
  const ring = tone === "cyan" ? "border-cyan-200 bg-cyan-50" : tone === "emerald" ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white";
  return (
    <div className={`rounded-xl p-3 border ${ring}`} title={tooltip}>
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <div className="flex items-baseline gap-3">
        <div><div className="text-[9px] font-black text-slate-400 uppercase">normal</div><div className="text-sm font-black text-slate-900 tabular-nums">{fmtH(t.normal)}</div></div>
        <div><div className="text-[9px] font-black text-amber-500 uppercase">OT 50</div><div className="text-sm font-black text-amber-700 tabular-nums">{fmtH(t.ot50)}</div></div>
        <div><div className="text-[9px] font-black text-rose-500 uppercase">OT 100</div><div className="text-sm font-black text-rose-700 tabular-nums">{fmtH(t.ot100)}</div></div>
      </div>
    </div>
  );
}

function CostCard({ data }) {
  const nok = (n) => `${(n || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr`;
  const cost = data.cost_estimates || {};
  const paid = cost.paid_in_month || {};
  const actual = cost.actual_in_month || {};
  const unpaid = (data.deltas?.actual_vs_paid_cost) || 0;
  return (
    <div className="rounded-2xl border border-cyan-200 bg-gradient-to-br from-cyan-50/60 via-white to-amber-50/60 p-4" data-testid="ot-audit-cost">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-cyan-700">Cost estimat angajator</div>
          <div className="text-sm font-black text-slate-900">{data.worker_name} — {data.month}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">tarif/oră</div>
          <div className="text-base font-black text-slate-900 tabular-nums">{nok(data.hourly_cost)}</div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <CostBox title="Dacă s-ar aplica doar regula săptămânală" bucket={cost.weekly_rule} nok={nok} tone="slate" />
        <CostBox title="Calcul final (actual)" bucket={actual} nok={nok} tone="cyan" highlight />
        <CostBox title="Plătit (doar zile blocate)" bucket={paid} nok={nok} tone="emerald" />
      </div>
      {unpaid > 0 && (
        <div className="mt-3 flex items-start gap-2 text-xs font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <span className="font-black">{nok(unpaid)}</span> din costul total NU este încă plătit (OT pe zile neblocate). După blocarea zilelor, acest cost va fi inclus pe fluturaș.
          </div>
        </div>
      )}
    </div>
  );
}

function CostBox({ title, bucket, nok, tone, highlight }) {
  const ring = highlight ? "border-cyan-400 bg-white shadow-sm" : tone === "emerald" ? "border-emerald-200 bg-white" : "border-slate-200 bg-white";
  const b = bucket || {};
  return (
    <div className={`rounded-xl p-3 border ${ring}`}>
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">{title}</div>
      <div className="space-y-1 text-xs">
        <RowLine label="Normal" hours={b.normal_hours} cost={b.normal_cost} nok={nok} tone="slate" />
        <RowLine label="OT 50% (×1.5)" hours={b.ot50_hours} cost={b.ot50_cost} nok={nok} tone="amber" />
        <RowLine label="OT 100% (×2)" hours={b.ot100_hours} cost={b.ot100_cost} nok={nok} tone="rose" />
      </div>
      <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between">
        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Total</span>
        <span className="text-sm font-black text-slate-900 tabular-nums">{nok(b.total_cost)}</span>
      </div>
    </div>
  );
}

function RowLine({ label, hours, cost, nok, tone }) {
  const color = tone === "amber" ? "text-amber-700" : tone === "rose" ? "text-rose-700" : "text-slate-700";
  return (
    <div className="flex items-center justify-between">
      <span className={`font-bold ${color}`}>{label}</span>
      <span className="flex items-center gap-2">
        <span className="tabular-nums text-slate-500">{(hours || 0).toFixed(2)}h</span>
        <span className="tabular-nums font-bold text-slate-900">{nok(cost || 0)}</span>
      </span>
    </div>
  );
}

function WeekCard({ wk }) {
  const [expanded, setExpanded] = useState(wk.deltas.length > 0);
  const totalWorked = wk.days.reduce((s, d) => s + (d.worked_minutes || 0), 0);
  const otActual = wk.totals_actual.ot50 + wk.totals_actual.ot100;
  const otPaid = wk.totals_paid_in_month.ot50 + wk.totals_paid_in_month.ot100;
  // Per-month OT split for this week — tells the admin at a glance which
  // month each OT hour on this week falls under (payslip attribution).
  const otByMonth = useMemo(() => {
    const by = {};
    for (const d of wk.days) {
      const ot = (d.actual?.ot50 || 0) + (d.actual?.ot100 || 0);
      if (!ot) continue;
      const key = (d.date || "").slice(0, 7);
      by[key] = (by[key] || 0) + ot;
    }
    return Object.entries(by).sort(([a], [b]) => a.localeCompare(b));
  }, [wk]);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <button type="button" onClick={() => setExpanded(!expanded)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50">
        <div className="flex-1 flex items-center gap-3">
          <div className="text-[11px] font-black uppercase tracking-widest text-slate-500">
            {fmtDate(wk.monday)} – {fmtDate(wk.sunday)}
          </div>
          {wk.spills_next_month && (<span className="text-[9px] font-black uppercase bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded">daily_mode</span>)}
          <span className="text-[11px] font-black text-slate-700">Lucrate: <span className="tabular-nums">{fmtH(totalWorked)}</span></span>
          <span className="text-[11px] font-black text-amber-700">OT calculat: <span className="tabular-nums">{fmtH(otActual)}</span></span>
          {otPaid !== otActual && (<span className="text-[11px] font-black text-rose-700">OT plătit: <span className="tabular-nums">{fmtH(otPaid)}</span></span>)}
          {wk.deltas.length > 0 && (<span className="text-[9px] font-black uppercase bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />diferență</span>)}
        </div>
        <span className="text-xs text-slate-400">{expanded ? "▲" : "▼"}</span>
      </button>
      {expanded && (
        <div className="border-t border-slate-100">
          {otByMonth.length > 1 && (
            <div className="px-4 py-2 bg-amber-50/60 border-b border-amber-100 flex items-center gap-2 flex-wrap">
              <span className="text-[9px] font-black uppercase tracking-widest text-amber-800">Împărțit pe luni:</span>
              {otByMonth.map(([m, mins]) => (
                <span key={m} className="inline-flex items-center text-[11px] font-black bg-white border border-amber-200 text-amber-700 px-2 py-0.5 rounded-full tabular-nums">
                  {fmtH(mins)} în {m}
                </span>
              ))}
            </div>
          )}
          {/* Day table */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[9px] font-black uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left py-2 px-3">Zi</th>
                  <th className="text-right px-2">Lucrate</th>
                  <th className="text-right px-2">Normal</th>
                  <th className="text-right px-2">OT 50</th>
                  <th className="text-right px-2">OT 100</th>
                  <th className="text-center px-2">Blocată</th>
                  <th className="text-left px-3">Note</th>
                </tr>
              </thead>
              <tbody>
                {wk.days.map((d, i) => {
                  const inMonth = d.in_month;
                  const edited = d.entries.some((e) => e.edited);
                  const notes = [];
                  if (d.has_vacation) notes.push("concediu");
                  if (d.has_sick) notes.push("boală");
                  if (d.weekday === 5) notes.push("sâmbătă");
                  if (d.weekday === 6) notes.push("duminică");
                  if (edited) notes.push("editat");
                  return (
                    <tr key={d.date} className={`${inMonth ? "" : "opacity-40"} ${i < wk.days.length - 1 ? "border-b border-slate-100" : ""}`}>
                      <td className="py-1.5 px-3 font-black text-slate-800">{WD_SHORT[d.weekday]} {fmtDate(d.date)}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums">{fmtH(d.worked_minutes)}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums text-slate-700">{d.actual.normal ? fmtH(d.actual.normal) : "—"}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums text-amber-700">{d.actual.ot50 ? fmtH(d.actual.ot50) : "—"}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums text-rose-700">{d.actual.ot100 ? fmtH(d.actual.ot100) : "—"}</td>
                      <td className="py-1.5 px-2 text-center">{inMonth ? (d.locked ? <Lock className="w-3.5 h-3.5 text-emerald-600 inline" /> : <Unlock className="w-3.5 h-3.5 text-slate-400 inline" />) : <span className="text-[9px] text-slate-300">afara lunii</span>}</td>
                      <td className="py-1.5 px-3 text-[10px] text-slate-500">
                        {notes.map((n, j) => (<span key={j} className="inline-block mr-1 bg-slate-100 text-slate-600 px-1 rounded">{n}</span>))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {wk.deltas.length > 0 && (
            <div className="px-4 py-3 bg-amber-50/60 border-t border-amber-100 space-y-1.5">
              {wk.deltas.map((dlt, i) => (
                <div key={i} className="flex items-start gap-2 text-[11px]">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-black text-amber-900">{fmtH(dlt.minutes)} — {dlt.reason}</div>
                    {dlt.dates && dlt.dates.length > 0 && (<div className="text-[10px] text-amber-700">Zile: {dlt.dates.map(fmtDate).join(", ")}</div>)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EditLogSection({ weeks }) {
  const allEdits = weeks.flatMap((w) => (w.edit_log || []).map((e) => ({ ...e, _week: w.monday })));
  const allEdited = weeks.flatMap((w) => (w.edited_entries || []).map((e) => ({ ...e, _week: w.monday })));
  if (allEdits.length === 0 && allEdited.length === 0) return null;
  return (
    <details className="rounded-2xl border border-slate-200 bg-white overflow-hidden" open={allEdits.length > 0}>
      <summary className="cursor-pointer px-4 py-3 flex items-center gap-2 hover:bg-slate-50 text-sm font-black text-slate-800">
        <History className="w-4 h-4 text-cyan-700" />
        Modificări înregistrate ({allEdits.length + allEdited.length})
      </summary>
      {allEdits.length > 0 && (
        <div className="border-t border-slate-100 p-3 space-y-2">
          <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Jurnal modificări (de la nov. 2026)</div>
          {allEdits.map((ed) => (
            <div key={ed.id} className="rounded-lg bg-slate-50 border border-slate-200 p-2.5 text-xs">
              <div className="flex items-center gap-2 text-[10px] text-slate-500">
                <Pencil className="w-3 h-3" />
                <span className="font-black text-slate-700">{ed.edited_by}</span>
                <span>•</span>
                <span>{fmtTs(ed.edited_at)}</span>
              </div>
              <div className="mt-1 font-bold text-slate-800">
                {fmtDate(ed.old_date)} <span className="text-slate-400">{ed.old_from_time}→{ed.old_to_time}</span> <span className="tabular-nums text-slate-700">{fmtH(ed.old_minutes)}</span>
                <span className="mx-1.5 text-slate-400">⇒</span>
                {ed.new_kind === "deleted" ? (<span className="text-rose-700 font-black">ȘTERSĂ</span>) : (
                  <>{fmtDate(ed.new_date)} <span className="text-slate-400">{ed.new_from_time}→{ed.new_to_time}</span> <span className="tabular-nums text-slate-700">{fmtH(ed.new_minutes)}</span></>
                )}
              </div>
              {(ed.old_project_name || ed.new_project_name) && ed.old_project_name !== ed.new_project_name && (
                <div className="text-[10px] text-slate-500 mt-0.5">
                  Proiect: {ed.old_project_name || "—"} → {ed.new_project_name || "—"}
                </div>
              )}
              <div className="text-[10px] text-slate-400 mt-0.5">Δ minute: <span className={ed.new_minutes - ed.old_minutes < 0 ? "text-rose-700 font-black" : "text-emerald-700 font-black"}>{fmtH(ed.new_minutes - ed.old_minutes)}</span></div>
            </div>
          ))}
        </div>
      )}
      {allEdited.length > 0 && (
        <div className="border-t border-slate-100 p-3 space-y-1">
          <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Intrări editate după creare (fără detalii înainte)</div>
          {allEdited.map((e) => (
            <div key={e.id} className="text-[11px] text-slate-700">
              <span className="font-black">{fmtDate(e.date)}</span> · <span className="tabular-nums">{fmtH(e.minutes)}</span>
              {e.project_name && (<span className="text-slate-500"> · {e.project_name}</span>)}
              <span className="text-slate-400"> · modificată {fmtTs(e.updated_at)}</span>
            </div>
          ))}
        </div>
      )}
    </details>
  );
}

/**
 * OtRulesInfo — a dedicated popover-style modal listing EVERY criterion the
 * backend applies when turning raw time entries into normal + OT 50 + OT 100.
 * Shared between the Audit modal (next to the title) and the Control Contabil
 * toolbar (via `<OtRulesButton />`), so the admin always has a single source
 * of truth for how hours are counted.
 */
export function OtRulesInfo({ onClose }) {
  return (
    <div className="fixed inset-0 z-[95] bg-slate-900/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-auto" onClick={onClose} data-testid="ot-rules-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-gradient-to-r from-cyan-50 via-white to-amber-50">
          <div className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-cyan-700" />
            <h2 className="text-base font-black text-slate-900">Cum se calculează orele</h2>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600" data-testid="ot-rules-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4 text-[13px] text-slate-700 leading-relaxed">
          <RuleSection title="Pragul săptămânal" tone="cyan">
            <li><b>37.5h / săptămână</b> este pragul standard (Mon–Sun). Tot ce depășește ⇒ ore suplimentare.</li>
            <li>Orele de depozit (<b>Lager</b>) consumă pragul <b>primele</b>, până la 7.5h/zi (restul merge pe cont de depozit, nu intră în OT).</li>
            <li>Săptămână = Luni–Duminică (ISO). Weekendul face parte din aceeași săptămână dacă se încadrează în interval.</li>
          </RuleSection>
          <RuleSection title="Procentele de OT" tone="amber">
            <li><b>OT 50%</b> (×1.5) — pentru ore peste 7.5h/zi SAU peste 37.5h/săpt. Sâmbăta e OT doar dacă săptămâna depășește 37.5h.</li>
            <li><b>OT 100%</b> (×2.0) — orele lucrate <b>duminica</b> sau pe zile <b>roșii</b> (sărbători norvegiene) sunt ÎNTOTDEAUNA 100%, chiar dacă săptămâna nu atinge 37.5h.</li>
            <li>Orele pe zile roșii nu intră în pragul 37.5h ⇒ pot aduce OT mai devreme în săptămână.</li>
          </RuleSection>
          <RuleSection title="Distribuția orelor OT (ordinea)" tone="emerald">
            <li>Faza 1 (cronologic): fiecare zi care depășește 7.5h/zi primește imediat excesul ca OT 50%.</li>
            <li>Faza 2 (invers cronologic): surplusul săptămânal rămas se adaugă pe ultima zi lucrată (de regulă weekendul).</li>
            <li>Dacă săptămâna e la limita unei luni, OT-ul pică acolo unde a fost lucrată ora (atribuire per lună).</li>
          </RuleSection>
          <RuleSection title="Pauze" tone="slate">
            <li>Pauzele <b>plătite</b> (cod de pauză permis) NU împing săptămâna în OT — contează ca ore lucrate normale, cu badge ☕.</li>
            <li>Pauzele <b>neplătite</b> se scad din orele brute înainte de calculul OT.</li>
            <li>Auto-break: zilele peste 7.5h fără cod de pauză manual primesc −30 min automat.</li>
          </RuleSection>
          <RuleSection title="Închiderea de lună (daily_mode)" tone="violet">
            <li>Când o săptămână <b>debordează</b> în luna următoare (ne-închisă încă) ȘI luna curentă nu are încă 37.5h raportate, se aplică regula zilnică (&gt;7.5h/zi) ca să nu se piardă OT-ul.</li>
            <li>Dacă săptămâna are deja ≥ 37.5h raportate în luna de închidere, se aplică regula săptămânală clasică (fix important din noi. 2026).</li>
            <li>Orele deja plătite pe fluturaș (zile <b>blocate</b>) nu se recalculează.</li>
          </RuleSection>
          <RuleSection title="Vacanță, boală, sărbători" tone="rose">
            <li>Concediul și boala NU sunt ore lucrate ⇒ nu contează la pragul 37.5h.</li>
            <li>Astfel o săptămână cu Luni-concediu + Sâm-lucrat 10h NU devine automat OT: săptămâna total e doar 10h, sub prag.</li>
          </RuleSection>
          <RuleSection title="Plată efectivă (payslip)" tone="cyan">
            <li>Doar <b>zilele blocate</b> contribuie la fluturaș. OT-ul calculat pentru zile neblocate apare în audit ca „neplătit încă".</li>
            <li>Tariful/oră e luat din profilul angajatului: brut + feriepenger + pensiune + AGA (sau tarif manual).</li>
            <li>Cost estimat = Normal × tarif + OT50 × tarif × 1.5 + OT100 × tarif × 2.</li>
          </RuleSection>
          <div className="pt-2 border-t border-slate-100 text-[11px] text-slate-400">
            Toate regulile sunt aplicate în `routes/doserne_timesheet.py::_split_week_ot`. Pentru detalii și validări pe scenarii concrete, generează PDF audit pentru un angajat.
          </div>
        </div>
      </div>
    </div>
  );
}

function RuleSection({ title, tone, children }) {
  const tones = {
    cyan: "border-cyan-200 bg-cyan-50/50",
    amber: "border-amber-200 bg-amber-50/50",
    emerald: "border-emerald-200 bg-emerald-50/50",
    slate: "border-slate-200 bg-slate-50/50",
    violet: "border-violet-200 bg-violet-50/50",
    rose: "border-rose-200 bg-rose-50/50",
  };
  return (
    <div className={`rounded-xl border p-3 ${tones[tone] || tones.slate}`}>
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-2">{title}</div>
      <ul className="list-disc list-outside pl-4 space-y-1 text-[12px]">{children}</ul>
    </div>
  );
}

